import { gql, useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { SpeciesEnum } from "../../__generated__/globalTypes";
import { isDefined } from "../utils/utils";
import { UseBreeds, UseBreedsVariables } from "../../__generated__/UseBreeds";

export type Breed = {
  id: string;
  name: string;
  lifeAgeGroup: number;
  group: number;
  lifeGroup: number;
};

function useBreeds({
  species,
  topBreeds,
  excludeBreeds,
}: {
  species: SpeciesEnum | null;
  topBreeds?: string;
  excludeBreeds?: string;
}) {
  const [breeds, setBreeds] = useState<Breed[]>([]);

  const [getBreeds, { loading: breedsLoading }] = useLazyQuery<
    UseBreeds,
    UseBreedsVariables
  >(GET_BREEDS, {
    variables: {
      input: {
        limit: 100000,
        species,
      },
    },
  });

  useEffect(() => {
    if (species) {
      (async () => {
        const result = await getBreeds();

        setBreeds(
          (result.data?.getBreeds || [])
            .filter(isDefined)
            .filter((b) => !excludeBreeds || !excludeBreeds.includes(b.id))
            .sort((a, b) => {
              if (!topBreeds) {
                return 0;
              }
              if (topBreeds.includes(a.id) && topBreeds.includes(b.id)) {
                return topBreeds.indexOf(a.id) < topBreeds.indexOf(b.id)
                  ? -1
                  : 0;
              }

              return topBreeds.includes(a.id) ? -1 : 0;
            })
            .map((b) => ({
              id: b.id,
              name: b.name ?? "",
              lifeAgeGroup: b.lifeAgeGroup || -1,
              group: b.group || -1,
              lifeGroup: b.lifeGroup || -1,
            }))
        );
      })();
    }
  }, [excludeBreeds, getBreeds, species, topBreeds]);

  return { breeds, breedsLoading };
}

const GET_BREEDS = gql`
  query UseBreeds($input: GetBreedsInput) {
    getBreeds(input: $input) {
      name
      id
      lifeAgeGroup
      group
      lifeGroup
    }
  }
`;

export default useBreeds;
